import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, MapPin, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "@/lib/useSite";
import { api } from "@/lib/api";
import Chapter from "@/components/Chapter";

const inputCls =
  "w-full rounded-none border-0 border-b border-ink/20 bg-transparent px-0 py-3.5 text-sm text-ink outline-none transition-colors placeholder:text-subtle focus:border-srs";

export default function Contact() {
  const { data } = useSite();
  const s = data?.settings || {};
  const products = data?.products || [];
  const phone = s.phone || "6366634517";
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState(() => ({
    name: "",
    phone: "",
    product_interest: sessionStorage.getItem("srs_enquiry_product") || "",
    quantity: "",
    location: "",
  }));

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setSending(true);
    try {
      await api.post("/enquiries", form);
      sessionStorage.removeItem("srs_enquiry_product");
      toast.success("Enquiry received. We'll call you back shortly.");
      setForm({ name: "", phone: "", product_interest: "", quantity: "", location: "" });
    } catch (err) {
      toast.error("Could not send the enquiry. Please call us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <Chapter
        no="05"
        kicker="Estimate"
        heading="Get a free estimate."
        sub="Tell us what you're building. We call back with a straight quote — usually the same day."
      />
      <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <a
            data-testid="click-to-call-button"
            href={`tel:+91${phone}`}
            className="group flex items-center gap-5 rounded-3xl border border-ink/10 bg-white p-6 shadow-[0_16px_40px_-24px_rgba(13,13,17,0.22)] transition-shadow duration-500 hover:shadow-[0_26px_55px_-24px_rgba(13,13,17,0.32)] transition-colors hover:border-srs"
          >
            <span className="flex h-14 w-14 shrink-0 items-center justify-center bg-srs text-white transition-transform group-hover:scale-105">
              <Phone size={22} />
            </span>
            <span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-subtle">Call the yard directly</span>
              <span className="mt-1 block font-display text-3xl font-extrabold tracking-tight text-ink group-hover:text-srs">
                +91 {phone}
              </span>
            </span>
          </a>

          <div className="mt-6 flex items-start gap-5 rounded-3xl border border-ink/10 bg-white p-6 shadow-[0_16px_40px_-24px_rgba(13,13,17,0.22)] transition-shadow duration-500 hover:shadow-[0_26px_55px_-24px_rgba(13,13,17,0.32)]">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center border border-line text-ink">
              <MapPin size={22} />
            </span>
            <div>
              <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-subtle">Visit us</span>
              <p data-testid="contact-address" className="mt-1 text-base font-medium leading-relaxed text-ink">
                {s.address || "Karimane Gate, Sugar Factory Road, Mandya, Karnataka"}
              </p>
              {s.whatsapp && (
                <a
                  data-testid="whatsapp-link"
                  href={`https://wa.me/${s.whatsapp.replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-srs hover:text-srs-dark"
                >
                  <MessageCircle size={14} /> Message on WhatsApp
                </a>
              )}
            </div>
          </div>

          <div className="mt-6 overflow-hidden rounded-[2rem] border border-ink/10">
            <iframe
              data-testid="contact-map"
              title="SRS – Sun Roofing Solutions on the map"
              src={`https://www.google.com/maps?q=${encodeURIComponent(s.map_query || "Karimane Gate, Sugar Factory Road, Mandya, Karnataka")}&output=embed`}
              className="h-72 w-full grayscale transition-all duration-500 hover:grayscale-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </motion.div>

        <motion.form
          data-testid="enquiry-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="rounded-[2.5rem] border border-ink/10 bg-white p-7 shadow-[0_30px_80px_-50px_rgba(13,13,17,0.25)] sm:p-12"
        >
          <p className="mb-8 font-mono text-[11px] uppercase tracking-[0.25em] text-subtle">Quick enquiry — 30 seconds</p>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="enq-name" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-mute">Name *</label>
              <input id="enq-name" data-testid="enquiry-form-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" required />
            </div>
            <div>
              <label htmlFor="enq-phone" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-mute">Phone *</label>
              <input id="enq-phone" data-testid="enquiry-form-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="98765 43210" inputMode="tel" required />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="enq-product" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-mute">Product interest</label>
              <select id="enq-product" data-testid="enquiry-form-category-select" className={inputCls} value={form.product_interest} onChange={set("product_interest")}>
                <option value="">Select a category</option>
                {products.map((p) => (
                  <option key={p.id} value={p.title}>{p.title}</option>
                ))}
                <option value="Other / Multiple">Other / Multiple</option>
              </select>
            </div>
            <div>
              <label htmlFor="enq-qty" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-mute">Quantity</label>
              <input id="enq-qty" data-testid="enquiry-form-quantity-input" className={inputCls} value={form.quantity} onChange={set("quantity")} placeholder="e.g. 1200 sq.ft" />
            </div>
            <div>
              <label htmlFor="enq-loc" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-mute">Location</label>
              <input id="enq-loc" data-testid="enquiry-form-location-input" className={inputCls} value={form.location} onChange={set("location")} placeholder="Town / village" />
            </div>
          </div>
          <button
            data-testid="enquiry-form-submit-button"
            type="submit"
            disabled={sending}
            className="mt-8 w-full rounded-full bg-ink px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors hover:bg-srs disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send enquiry"}
          </button>
          <p className="mt-4 text-center text-xs text-subtle">No spam, no OTP — we simply call you back.</p>
        </motion.form>
      </div>
    </div>
  );
}
